import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, X } from "lucide-react";
import CoutureLayout from "./CoutureLayout";
import { api, formatApiError, formatPrice } from "../../lib/api";
import { useI18n } from "../../i18n/I18nContext";

export default function CoutureOrderForm() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [fields, setFields] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        client_name: "",
        client_phone: "",
        client_email: "",
        category_id: "",
        subcategory_id: "",
        description: "",
        measurements: {},
        price_fcfa: "",
        cost_fcfa: "",
        cost_lines: [],
        price_eur: "",
        deposit_fcfa: "",
        due_date: "",
    });

    useEffect(() => {
        api.get("/couture/categories").then((r) => setCategories(r.data)).catch(() => {});
        api.get("/couture/measurement-fields").then((r) => setFields(r.data)).catch(() => {});
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setMeasure = (key) => (e) =>
        setForm((f) => ({ ...f, measurements: { ...f.measurements, [key]: e.target.value } }));

    const subcategories = categories.filter((c) => c.parent_id === form.category_id);

    const applyCategory = (cat, next) => {
        if (!cat) return next;
        if (cat.price_fcfa != null) next.price_fcfa = String(cat.price_fcfa);
        if (cat.cost_lines && cat.cost_lines.length) {
            next.cost_lines = cat.cost_lines.map((l) => ({ label: l.label || "", amount: String(l.amount ?? "") }));
        } else if (cat.cost_fcfa != null) {
            next.cost_lines = [{ label: t("couture.cost_label_ph"), amount: String(cat.cost_fcfa) }];
        }
        return next;
    };

    const onCategoryChange = (e) => {
        const id = e.target.value;
        setForm((f) => {
            const next = { ...f, category_id: id, subcategory_id: "", cost_lines: [] };
            const cat = categories.find((c) => c.id === id);
            return applyCategory(cat, next);
        });
    };

    const onSubcategoryChange = (e) => {
        const id = e.target.value;
        setForm((f) => {
            const next = { ...f, subcategory_id: id, cost_lines: [] };
            const sub = categories.find((c) => c.id === id);
            return applyCategory(sub, next);
        });
    };

    const addLine = () => setForm((f) => ({ ...f, cost_lines: [...f.cost_lines, { label: "", amount: "" }] }));
    const setLine = (i, key, val) =>
        setForm((f) => ({ ...f, cost_lines: f.cost_lines.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)) }));
    const removeLine = (i) => setForm((f) => ({ ...f, cost_lines: f.cost_lines.filter((_, idx) => idx !== i) }));

    const costTotal = form.cost_lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const hasCost = form.cost_lines.length > 0 || (form.cost_fcfa !== "" && form.cost_fcfa != null);
    const effectiveCost = form.cost_lines.length > 0 ? costTotal : (Number(form.cost_fcfa) || 0);
    const profit = (Number(form.price_fcfa) || 0) - effectiveCost;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                ...form,
                client_email: form.client_email || null,
                category_id: form.category_id || null,
                subcategory_id: form.subcategory_id || null,
                price_fcfa: Number(form.price_fcfa) || 0,
                cost_fcfa: form.cost_lines.length ? costTotal : (form.cost_fcfa !== "" ? Number(form.cost_fcfa) : null),
                cost_lines: form.cost_lines.filter((l) => l.label.trim() !== "").map((l) => ({ label: l.label.trim(), amount: Number(l.amount) || 0 })),
                price_eur: form.price_eur ? Number(form.price_eur) : null,
                deposit_fcfa: Number(form.deposit_fcfa) || 0,
                due_date: form.due_date || null,
                measurements: Object.fromEntries(
                    Object.entries(form.measurements).filter(([, v]) => v !== "" && v != null)
                ),
            };
            const { data } = await api.post("/couture/orders", payload);
            toast.success(t("couture.created"));
            navigate(`/couturier/commandes/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const inputCls =
        "w-full mt-1.5 bg-white border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-brand-gold transition";
    const labelCls = "text-[0.65rem] tracking-[0.22em] uppercase font-bold text-gray-500";

    return (
        <CoutureLayout>
            <div className="p-6 md:p-10 max-w-4xl" data-testid="couture-order-form-page">
                <Link to="/couturier/commandes" className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-brand-gold mb-6">
                    <ArrowLeft className="w-4 h-4" /> {t("couture.orders")}
                </Link>
                <h1 className="font-display text-3xl text-brand-navy">{t("couture.new_order")}</h1>
                <div className="w-12 h-px bg-brand-gold mt-2 mb-8" />

                <form onSubmit={submit} className="space-y-8">
                    {/* Client */}
                    <section className="bg-white border border-gray-100 p-6">
                        <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">
                            {t("couture.section_client")}
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <label className="block">
                                <span className={labelCls}>{t("couture.client_name")} *</span>
                                <input required value={form.client_name} onChange={set("client_name")} data-testid="couture-form-name" className={inputCls} />
                            </label>
                            <label className="block">
                                <span className={labelCls}>{t("couture.client_phone")}</span>
                                <input value={form.client_phone} onChange={set("client_phone")} data-testid="couture-form-phone" className={inputCls} />
                            </label>
                            <label className="block sm:col-span-2">
                                <span className={labelCls}>{t("couture.client_email")}</span>
                                <input type="email" value={form.client_email} onChange={set("client_email")} data-testid="couture-form-email" className={inputCls} />
                            </label>
                        </div>
                    </section>

                    {/* Category + details */}
                    <section className="bg-white border border-gray-100 p-6">
                        <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">
                            {t("couture.section_model")}
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <label className="block">
                                <span className={labelCls}>{t("couture.category")}</span>
                                <select value={form.category_id} onChange={onCategoryChange} data-testid="couture-form-category" className={inputCls}>
                                    <option value="">—</option>
                                    {categories.filter((c) => !c.parent_id).map((c) => (
                                        <option key={c.id} value={c.id}>{c.name_fr}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block">
                                <span className={labelCls}>{t("couture.subcategory")}</span>
                                <select
                                    value={form.subcategory_id}
                                    onChange={onSubcategoryChange}
                                    disabled={!form.category_id}
                                    data-testid="couture-form-subcategory"
                                    className={inputCls + " disabled:opacity-50"}
                                >
                                    <option value="">—</option>
                                    {subcategories.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name_fr}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block sm:col-span-2">
                                <span className={labelCls}>{t("couture.description")}</span>
                                <textarea rows={3} value={form.description} onChange={set("description")} data-testid="couture-form-description" className={inputCls} />
                            </label>
                        </div>
                    </section>

                    {/* Measurements */}
                    {fields.length > 0 && (
                        <section className="bg-white border border-gray-100 p-6">
                            <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">
                                {t("couture.section_measurements")}
                            </h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                                {fields.map((f) => (
                                    <label key={f.key} className="block">
                                        <span className={labelCls}>
                                            {f.label_fr} {f.unit ? `(${f.unit})` : ""}
                                        </span>
                                        <input
                                            value={form.measurements[f.key] || ""}
                                            onChange={setMeasure(f.key)}
                                            data-testid={`couture-form-measure-${f.key}`}
                                            className={inputCls}
                                        />
                                    </label>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Price */}
                    <section className="bg-white border border-gray-100 p-6">
                        <h2 className="text-[0.7rem] tracking-[0.22em] uppercase font-bold text-brand-navy mb-5">
                            {t("couture.section_payment")}
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-5">
                            <label className="block">
                                <span className={labelCls}>{t("couture.price_fcfa")}</span>
                                <input type="number" min="0" value={form.price_fcfa} onChange={set("price_fcfa")} data-testid="couture-form-price" className={inputCls} />
                            </label>
                            <label className="block">
                                <span className={labelCls}>{t("couture.price_eur")}</span>
                                <input type="number" min="0" value={form.price_eur} onChange={set("price_eur")} data-testid="couture-form-price-eur" className={inputCls} />
                            </label>
                            <label className="block">
                                <span className={labelCls}>{t("couture.deposit_fcfa")}</span>
                                <input type="number" min="0" value={form.deposit_fcfa} onChange={set("deposit_fcfa")} data-testid="couture-form-deposit" className={inputCls} />
                            </label>
                            <label className="block">
                                <span className={labelCls}>{t("couture.due_date")}</span>
                                <input type="date" value={form.due_date} onChange={set("due_date")} data-testid="couture-form-due" className={inputCls} />
                            </label>
                        </div>

                        {/* Cost lines */}
                        <div className="mt-5">
                            <div className="flex items-center justify-between mb-2">
                                <span className={labelCls}>{t("couture.cost_lines")}</span>
                                <button type="button" onClick={addLine} data-testid="couture-form-add-line" className="inline-flex items-center gap-1.5 text-xs text-brand-gold hover:text-brand-gold-deep font-semibold uppercase tracking-wider">
                                    <Plus className="w-3.5 h-3.5" /> {t("couture.add_cost_line")}
                                </button>
                            </div>
                            {form.cost_lines.length === 0 && (
                                <p className="text-xs text-gray-400">{t("couture.cost_lines_hint")}</p>
                            )}
                            {form.cost_lines.map((l, i) => (
                                <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 mb-2 items-center">
                                    <input value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} placeholder={t("couture.cost_label_ph")} data-testid={`couture-form-line-label-${i}`} className={inputCls} />
                                    <input type="number" min="0" value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} placeholder="FCFA" data-testid={`couture-form-line-amount-${i}`} className={inputCls + " w-32"} />
                                    <button type="button" onClick={() => removeLine(i)} className="p-2 text-gray-400 hover:text-red-500">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            {hasCost && (
                                <div className="mt-3 flex items-center justify-between bg-brand-paper border border-gray-100 px-4 py-3" data-testid="couture-form-profit">
                                    <span className="text-[0.65rem] tracking-[0.2em] uppercase font-bold text-gray-500">
                                        {t("couture.cost")}: {formatPrice(effectiveCost, "FCFA")} · {t("couture.profit")}
                                    </span>
                                    <span className={`font-display text-lg ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                                        {profit.toLocaleString("fr-FR")} FCFA
                                    </span>
                                </div>
                            )}
                        </div>
                    </section>

                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="couture-form-submit"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-brand-gold hover:bg-brand-gold-deep disabled:opacity-60 text-white text-[0.72rem] tracking-[0.22em] uppercase font-semibold transition-colors"
                    >
                        {busy ? t("admin.saving") : t("couture.create_order")}
                    </button>
                </form>
            </div>
        </CoutureLayout>
    );
}
